import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logError } from "@/lib/logger";
import { ensureNullableString, ensureStringArray } from "@/lib/profileUtils";

export interface UserProfile {
  roles: string[];
  skills_wanted: string[];
  skills_avoided: string[];
  location: string | null;
  remote_ok: boolean;
  work_modes: string[];
  assignment_types: string[];
  seniority: string | null;
  can_travel?: boolean;
  onboarding_completed: boolean;
  full_name: string | null;
  email: string | null;
  linkedin_url: string | null;
  phone: string | null;
  portfolio_url: string | null;
  // CV data for cover letter generation
  cv_extracted_skills: string[];
  headline: string | null;
  bio: string | null;
}

export const useUserProfile = () => {
  return useQuery({
    queryKey: ["user-profile"],
    queryFn: async (): Promise<UserProfile | null> => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        return null;
      }

      const { data: profile, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", session.user.id)
        .single();

      if (error) {
        logError("Error fetching profile", error);
        throw error;
      }

      return {
        roles: ensureStringArray(profile.roles),
        skills_wanted: ensureStringArray(profile.skills_wanted),
        skills_avoided: ensureStringArray(profile.skills_avoided),
        location: ensureNullableString(profile.location),
        remote_ok: profile.remote_ok ?? true,
        work_modes: ensureStringArray(profile.work_modes),
        assignment_types: ensureStringArray(profile.assignment_types),
        seniority: profile.seniority ?? null,
        can_travel: profile.can_travel ?? false,
        onboarding_completed: profile.onboarding_completed ?? false,
        full_name: ensureNullableString(profile.full_name),
        email: ensureNullableString(profile.email),
        linkedin_url: ensureNullableString(profile.linkedin_url),
        phone: ensureNullableString(profile.phone),
        portfolio_url: ensureNullableString(profile.portfolio_url),
        cv_extracted_skills: ensureStringArray(profile.cv_extracted_skills),
        headline: ensureNullableString(profile.headline),
        bio: ensureNullableString(profile.bio),
      };
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
    gcTime: 1000 * 60 * 60, // Keep in cache for 1 hour
  });
};
